import { Link } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { trackCta } from '@/lib/analytics';
import cover from '@/assets/sample/sample-cover.webp';
import glance from '@/assets/sample/sample-glance.webp';
import goals from '@/assets/sample/sample-goals.webp';
import { CLINICAL_LEAD, TESTIMONIALS, publishableLead, publishableTestimonials, type ClinicalLead as Lead, type Testimonial } from './content';

/** The whole sample, as a parent receives it: a made-up child, every page, nothing locked. */
export const SAMPLE_REPORT_URL = '/sample/nurture-sample-report.pdf';

const PAGES = [
  { src: cover, key: 'proof.sample.page1' },
  { src: glance, key: 'proof.sample.page2' },
  { src: goals, key: 'proof.sample.page3' },
] as const;

/** The real report, one tap from the first screen: three pages to look at and the full PDF. */
export const SampleReport = ({ id = 'sample', from }: { id?: string; from: string }) => {
  const t = useT();
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="mx-auto w-full max-w-content scroll-mt-24 px-4 py-14 md:px-6 nav:py-20">
      <div className="grid items-center gap-10 nav:grid-cols-[1fr_1.25fr]">
        <div>
          <p className="eyebrow text-coral-600">{t('proof.sample.eyebrow')}</p>
          <h2 id={`${id}-h`} className="mt-2 text-display">{t('proof.sample.title')}</h2>
          <p className="mt-3 max-w-[36em] text-[17px] text-ink-soft">{t('proof.sample.body')}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={SAMPLE_REPORT_URL}
              target="_blank"
              rel="noopener"
              onClick={() => trackCta(`${from}_sample_pdf`)}
              className="inline-flex min-h-tap items-center rounded-pill bg-lagoon-600 px-6 text-label font-extrabold text-white hover:bg-lagoon-700"
            >
              {t('proof.sample.open')}
            </a>
            <span className="text-caption text-ink-muted">{t('proof.sample.size')}</span>
          </div>
          <p className="mt-4 max-w-[40em] text-caption text-ink-muted">{t('proof.sample.note')}</p>
        </div>
        <ul className="grid grid-cols-3 gap-3">
          {PAGES.map((page) => (
            <li key={page.key}>
              <a href={SAMPLE_REPORT_URL} target="_blank" rel="noopener" onClick={() => trackCta(`${from}_sample_page`)} className="block rounded-md">
                <img
                  src={page.src}
                  alt={t(page.key)}
                  width={560}
                  height={792}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full rounded-md border border-line bg-white shadow-card"
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/** The clinician behind the report, in their own words. Renders nothing until `CLINICAL_LEAD` is complete. */
export const ClinicalLead = ({ lead = CLINICAL_LEAD }: { lead?: Lead | null }) => {
  const t = useT();
  const { locale } = useLocale();
  const shown = publishableLead(lead);
  if (!shown) return null;
  return (
    <section aria-labelledby="clinician-h" className="mx-auto w-full max-w-content px-4 py-14 md:px-6 nav:py-20">
      <div className="grid items-start gap-8 rounded-lg border border-line bg-white p-6 md:p-10 nav:grid-cols-[auto_1fr]">
        {shown.photo && <img src={shown.photo} alt="" width={120} height={120} loading="lazy" className="h-[120px] w-[120px] rounded-full object-cover" />}
        <div>
          <p className="eyebrow text-coral-600">{t('proof.lead.eyebrow')}</p>
          <h2 id="clinician-h" className="mt-2 text-h2">{shown.name}</h2>
          <p className="mt-1 text-body text-ink-soft">{[shown.qualifications, shown.role].filter(Boolean).join(' · ')}</p>
          <blockquote className="mt-5 max-w-[44em] text-[17px] leading-relaxed text-ink">“{shown.statement[locale]}”</blockquote>
          <p className="mt-4 text-caption text-ink-muted">
            {t('proof.lead.signed', { registration: shown.registration, date: new Date(shown.signedOn).toLocaleDateString(locale === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) })}
          </p>
          <p className="mt-2 text-caption text-ink-muted">
            {t('proof.lead.notDiagnosis')}{' '}
            <Link to="/trust" className="text-lagoon-600 underline underline-offset-4">{t('proof.lead.more')}</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

/** What parents told us, with their permission. Renders nothing until there is a real one. */
export const Testimonials = ({ items = TESTIMONIALS }: { items?: Testimonial[] }) => {
  const t = useT();
  const { locale } = useLocale();
  const shown = publishableTestimonials(items);
  if (!shown.length) return null;
  return (
    <section aria-labelledby="parents-h" className="mx-auto w-full max-w-content px-4 py-14 md:px-6 nav:py-20">
      <h2 id="parents-h" className="text-display">{t('proof.parents.title')}</h2>
      <ul className="mt-8 grid gap-5 md:grid-cols-2 nav:grid-cols-3">
        {shown.slice(0, 6).map((item) => (
          <li key={item.quote.en} className="flex flex-col rounded-lg border border-line bg-white p-6">
            <blockquote className="text-body leading-relaxed text-ink">“{item.quote[locale]}”</blockquote>
            <p className="mt-4 text-label font-bold text-ink-soft">
              {item.by[locale]}
              {item.city ? `, ${item.city}` : ''}
            </p>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-caption text-ink-muted">{t('proof.parents.note')}</p>
    </section>
  );
};
